import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, User, LoginCredentials } from '../api/authApi';

export const useAuth = () => {
  const queryClient = useQueryClient();

  const cachedUser = queryClient.getQueryData<User>(['user']);

  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: ['user'],
    queryFn: authApi.getUser,
    retry: false,
    staleTime: Infinity,
    enabled: cachedUser === undefined,
    initialData: cachedUser,
  });

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
    onSuccess: async () => {
      // После успешного логина обновляем данные пользователя
      await queryClient.fetchQuery({
        queryKey: ['user'],
        queryFn: authApi.getUser,
      });
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['user'] });
    },
  });

  const login = async (username: string, password: string) => {
    // Проверяем, есть ли уже авторизованный пользователь
    if (user) {
      return user; // Возвращаем текущего пользователя
    }

    // Выполняем логин только если пользователя нет
    await loginMutation.mutateAsync({ username, password });
    return queryClient.getQueryData<User>(['user']); // Возвращаем нового пользователя после логина
  };

  return {
    user,
    isLoading: isUserLoading || loginMutation.isPending,
    error: loginMutation.isError
      ? (loginMutation.error instanceof Error ? loginMutation.error.message : 'Login failed')
      : null,
    login,
    logout: () => logoutMutation.mutate(),
    isAuthenticated: !!user,
  };
};
